import TimeAgo from 'javascript-time-ago';
import en from 'javascript-time-ago/locale/en.json';
import { create } from 'jss';
import rg4js from 'raygun4js';
import { Provider } from 'react-redux';
import { Router } from 'react-router-dom';

import { NoSsr } from '@material-ui/core';
import CssBaseline from '@material-ui/core/CssBaseline';
import {
  jssPreset,
  StylesProvider,
  ThemeProvider as ThemeProviderV4,
} from '@material-ui/styles';
import { ThemeProvider as ThemeProviderV5 } from '@mui/material/styles';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import { APP_VER } from './core/config';
import ErrorBoundary from './core/errors/ErrorBoundary';
import { isRaygunEnabled } from './core/errors/errors.lib';
import { RouteGuard } from './core/guards/RouteGuard';
import history from './core/history/@history';
import AppLayout from './core/layout/AppLayout';
import { mainTheme as mainThemeV4 } from './core/theme/mainTheme';
import { mainTheme as mainThemeV5 } from './core/theme_v5/mainTheme';
import UpdatesListenerWrapper from './main/elevators/websockets/updates-listener-wrapper/UpdatesListenerWrapper';
import store from './store';
import CookiePopup from './ui/components/cookie-popup/CookiePopup';
import FlashMessage from './ui/components/flash-messages/flash-message/FlashMessage';
import UpdateOverlay from './ui/components/update-overlay/UpdateOverlay';

if (isRaygunEnabled()) {
  rg4js('apiKey', isRaygunEnabled() as string);
  rg4js('enableCrashReporting', true);
  rg4js('setVersion', APP_VER);
}

TimeAgo.addDefaultLocale(en);

const jss = create({
  ...jssPreset(),
  insertionPoint: document.getElementById('jss-insertion-point') as HTMLElement,
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false, // default: true
      retry: 2,
    },
  },
});

function App() {
  return (
    <NoSsr>
      <StylesProvider jss={jss}>
        <Provider store={store}>
          <QueryClientProvider client={queryClient}>
            <ReactQueryDevtools initialIsOpen={false} position="bottom-right" />
            <UpdatesListenerWrapper />
            <Router history={history}>
              <RouteGuard>
                <ThemeProviderV4 theme={mainThemeV4}>
                  <ThemeProviderV5 theme={mainThemeV5}>
                    <CssBaseline />
                    <ErrorBoundary>
                      <AppLayout />
                    </ErrorBoundary>
                    <UpdateOverlay />
                    <CookiePopup />
                    <FlashMessage />
                  </ThemeProviderV5>
                </ThemeProviderV4>
              </RouteGuard>
            </Router>
          </QueryClientProvider>
        </Provider>
      </StylesProvider>
    </NoSsr>
  );
}

export default App;
